import { useState, useEffect, useCallback } from 'react'
import { api } from '../lib/api'
import toast from 'react-hot-toast'
import { Modal, Input, Select, Textarea, Empty } from '../components/UI'
import { useAuth } from '../hooks/useAuth'
import { can } from '../lib/permissions'

const money = n => '$' + (parseFloat(n) || 0).toFixed(2)
// decimal hours <-> h/m helpers
const toHM = (dec) => { const d = parseFloat(dec) || 0; const h = Math.floor(d); const m = Math.round((d - h) * 60); return m === 60 ? { h: h + 1, m: 0 } : { h, m } }
const fromHM = (h, m) => (parseInt(h) || 0) + (parseInt(m) || 0) / 60
const fmtHM = (dec) => { const { h, m } = toHM(dec); return `${h}h ${String(m).padStart(2,'0')}m` }
const STATUS_COLORS = { Paid: 'var(--accent3)', Due: '#F59E0B', Unpaid: '#EF4444' }

export default function Payroll() {
  const { user } = useAuth()
  const isOwner = user?.role === 'owner'
  const perms = user?.permissions
  const cEdit = isOwner || can(perms, 'payroll', 'edit', isOwner)
  const cCreate = isOwner || can(perms, 'payroll', 'create', isOwner)
  const cDelete = isOwner || can(perms, 'payroll', 'delete', isOwner)

  const [tab, setTab] = useState('timesheet')
  const [employees, setEmployees] = useState([])
  const [selEmp, setSelEmp] = useState('')
  const [entries, setEntries] = useState([])
  const [runs, setRuns] = useState([])
  const [loading, setLoading] = useState(false)

  // modals
  const [timeModal, setTimeModal] = useState(null)
  const [timeForm, setTimeForm] = useState({})
  const [runModal, setRunModal] = useState(null)
  const [runForm, setRunForm] = useState({})
  const [preview, setPreview] = useState(null)
  const [payModal, setPayModal] = useState(null)
  const [payForm, setPayForm] = useState({})

  const loadEmployees = useCallback(() => {
    api.payEmployees().then(setEmployees).catch(e => toast.error(e.message))
  }, [])
  useEffect(() => { loadEmployees() }, [loadEmployees])

  const loadEntries = useCallback((uid) => {
    if (!uid) { setEntries([]); return }
    setLoading(true)
    api.payTimeList(uid).then(setEntries).catch(e => toast.error(e.message)).finally(() => setLoading(false))
  }, [])
  useEffect(() => { loadEntries(selEmp) }, [selEmp, loadEntries])

  const loadRuns = useCallback(() => {
    setLoading(true)
    api.payRuns().then(setRuns).catch(e => toast.error(e.message)).finally(() => setLoading(false))
  }, [])
  useEffect(() => { if (tab === 'runs') loadRuns() }, [tab, loadRuns])

  const empName = id => employees.find(e => String(e.id) === String(id))?.full_name || '—'
  const selEmpObj = employees.find(e => String(e.id) === String(selEmp))

  // ---- time entry ----
  const openTimeAdd = () => { setTimeForm({ work_date: new Date().toISOString().slice(0, 10), h: '', m: '', note: '' }); setTimeModal('add') }
  const openTimeEdit = (e) => { const { h, m } = toHM(e.hours); setTimeForm({ ...e, work_date: e.work_date?.slice(0, 10), h, m }); setTimeModal('edit') }
  const saveTime = async () => {
    try {
      if (timeModal === 'add') {
        await api.payTimeAdd({ user_id: selEmp, work_date: timeForm.work_date, hours: fromHM(timeForm.h, timeForm.m), note: timeForm.note })
        toast.success('Time entry added')
      } else {
        await api.payTimeUpdate(timeForm.id, { work_date: timeForm.work_date, hours: fromHM(timeForm.h, timeForm.m), note: timeForm.note })
        toast.success('Updated')
      }
      setTimeModal(null); loadEntries(selEmp)
    } catch (e) { toast.error(e.message) }
  }
  const delTime = async (e) => {
    if (e.payroll_run_id) { toast.error('This entry is locked to a paid run'); return }
    if (!window.confirm('Delete this entry?')) return
    try { await api.payTimeDelete(e.id); loadEntries(selEmp) } catch (er) { toast.error(er.message) }
  }

  // ---- payroll run ----
  const openRunCreate = () => {
    const today = new Date().toISOString().slice(0, 10)
    setRunForm({ user_id: selEmp || '', period_start: today, period_end: today, bonus: '' })
    setPreview(null); setRunModal(true)
  }
  const doPreview = async () => {
    if (!runForm.user_id) { toast.error('Select an employee'); return }
    try {
      const p = await api.payRunPreview({ user_id: runForm.user_id, period_start: runForm.period_start, period_end: runForm.period_end, bonus: runForm.bonus, days_worked: runForm.days_worked })
      setPreview(p)
    } catch (e) { toast.error(e.message) }
  }
  const createRun = async () => {
    try {
      await api.payRunCreate({ user_id: runForm.user_id, period_start: runForm.period_start, period_end: runForm.period_end, bonus: runForm.bonus, days_worked: runForm.days_worked })
      toast.success('Payroll run created'); setRunModal(false); setPreview(null); loadRuns()
    } catch (e) { toast.error(e.message) }
  }
  const openPay = (r) => { setPayForm({ id: r.id, paid_date: new Date().toISOString().slice(0, 10), paid_note: '' }); setPayModal(r) }
  const markPaid = async () => {
    try {
      await api.payRunUpdate(payForm.id, { status: 'Paid', paid_date: payForm.paid_date, paid_note: payForm.paid_note })
      toast.success('Marked paid'); setPayModal(null); loadRuns()
    } catch (e) { toast.error(e.message) }
  }
  const setStatus = async (r, status) => {
    try { await api.payRunUpdate(r.id, { status }); loadRuns() } catch (e) { toast.error(e.message) }
  }
  const delRun = async (r) => {
    if (!window.confirm('Delete this run? Its time entries will be unlocked.')) return
    try { await api.payRunDelete(r.id); loadRuns() } catch (e) { toast.error(e.message) }
  }
  const downloadPayslip = (r) => {
    const token = localStorage.getItem('adops_token')
    window.open(`/api/payroll/payslip/${r.id}?token=${token}`, '_blank')
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="toolbar">
        <div className="toolbar-left" style={{ gap: 8 }}>
          <span className="toolbar-title">💵 Payroll</span>
          <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
            <button onClick={() => setTab('timesheet')} style={{ background: tab === 'timesheet' ? 'var(--accent)' : 'var(--bg3)', color: tab === 'timesheet' ? '#fff' : 'var(--text3)', border: '1px solid var(--border2)', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>⏱ Timesheet</button>
            <button onClick={() => setTab('runs')} style={{ background: tab === 'runs' ? 'var(--accent)' : 'var(--bg3)', color: tab === 'runs' ? '#fff' : 'var(--text3)', border: '1px solid var(--border2)', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>💰 Payroll Runs</button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {tab === 'timesheet' && selEmp && cCreate && <button className="btn btn-secondary" onClick={openTimeAdd}>+ Add Time</button>}
          {cCreate && <button className="btn btn-primary" onClick={openRunCreate}>+ New Payroll Run</button>}
        </div>
      </div>

      {tab === 'timesheet' && (
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          <div style={{ marginBottom: 16, maxWidth: 360 }}>
            <Select label="Employee" options={[{ value: '', label: '— Select employee —' }, ...employees.map(e => ({ value: e.id, label: `${e.full_name} (${e.pay_type || 'no pay type'}${e.pay_type && e.pay_type !== 'monthly' ? ' · $' + e.pay_rate + '/hr' : e.pay_type === 'monthly' ? ' · $' + e.pay_rate + '/mo' : ''})` }))]} value={selEmp} onChange={e => setSelEmp(e.target.value)} />
          </div>
          {!selEmp ? <Empty icon="⏱" title="Select an employee" sub="Choose an employee to view and log their time." />
            : loading ? <Empty icon="⏳" title="Loading..." />
              : entries.length === 0 ? <Empty icon="⏱" title="No time entries" sub="Add time entries for this employee." />
                : <table className="data-table"><thead><tr><th>Date</th><th>Hours</th><th>Note</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>{entries.map(e => (
                    <tr key={e.id}>
                      <td>{e.work_date?.slice(0, 10)}</td>
                      <td>{fmtHM(e.hours)}</td>
                      <td style={{ fontSize: 12 }}>{e.note || '—'}</td>
                      <td>{e.payroll_run_id ? <span style={{ fontSize: 11, color: 'var(--text3)' }}>🔒 In run #{e.payroll_run_id}</span> : <span style={{ fontSize: 11, color: 'var(--accent3)' }}>Unpaid</span>}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {cEdit && !e.payroll_run_id && <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => openTimeEdit(e)}>✏️</button>}
                          {cDelete && !e.payroll_run_id && <button className="btn btn-danger" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => delTime(e)}>🗑</button>}
                        </div>
                      </td>
                    </tr>
                  ))}</tbody>
                </table>}
        </div>
      )}

      {tab === 'runs' && (
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {loading ? <Empty icon="⏳" title="Loading..." />
            : runs.length === 0 ? <Empty icon="💰" title="No payroll runs" sub="Create a payroll run to pay an employee." />
              : <table className="data-table"><thead><tr><th>Employee</th><th>Period</th><th>Type</th><th>Hours</th><th>Gross</th><th>Bonus</th><th>Total</th><th>Status</th><th>Paid Date</th><th>Actions</th></tr></thead>
                <tbody>{runs.map(r => (
                  <tr key={r.id}>
                    <td><strong>{r.full_name}</strong></td>
                    <td style={{ fontSize: 12 }}>{r.period_start?.slice(0, 10)} → {r.period_end?.slice(0, 10)}</td>
                    <td style={{ fontSize: 12 }}>{r.pay_type}</td>
                    <td>{fmtHM(r.total_hours)}</td>
                    <td>{money(r.gross_amount)}</td>
                    <td>{money(r.bonus)}</td>
                    <td><strong>{money(r.total_amount)}</strong></td>
                    <td><span style={{ fontSize: 11, fontWeight: 700, color: STATUS_COLORS[r.status] || 'var(--text)' }}>{r.status}</span></td>
                    <td style={{ fontSize: 12 }}>{r.paid_date?.slice(0, 10) || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => downloadPayslip(r)}>📄 PDF</button>
                        {cEdit && r.status !== 'Paid' && <button className="btn btn-primary" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => openPay(r)}>Mark Paid</button>}
                        {cEdit && r.status !== 'Unpaid' && r.status !== 'Paid' && <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => setStatus(r, 'Unpaid')}>Unpaid</button>}
                        {cDelete && <button className="btn btn-danger" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => delRun(r)}>🗑</button>}
                      </div>
                    </td>
                  </tr>
                ))}</tbody>
              </table>}
        </div>
      )}

      {timeModal && <Modal title={timeModal === 'add' ? 'Add Time Entry' : 'Edit Time Entry'} onClose={() => setTimeModal(null)}
        footer={<><button className="btn btn-ghost" onClick={() => setTimeModal(null)}>Cancel</button><button className="btn btn-primary" onClick={saveTime}>Save</button></>}>
        <Input label="Date" type="date" value={timeForm.work_date || ''} onChange={e => setTimeForm(f => ({ ...f, work_date: e.target.value }))} />
        <div style={{display:'flex',gap:12}}>
          <div style={{flex:1}}><Input label="Hours" type="number" min="0" max="24" placeholder="3" value={timeForm.h ?? ''} onChange={e => setTimeForm(f => ({ ...f, h: e.target.value }))} /></div>
          <div style={{flex:1}}><Input label="Minutes" type="number" min="0" max="59" placeholder="15" value={timeForm.m ?? ''} onChange={e => setTimeForm(f => ({ ...f, m: e.target.value }))} /></div>
        </div>
        <div style={{fontSize:11,color:'var(--text3)',marginTop:-6,marginBottom:8}}>= {fmtHM(fromHM(timeForm.h, timeForm.m))} ({fromHM(timeForm.h, timeForm.m).toFixed(2)} decimal hours)</div>
        <Textarea label="Note (optional)" value={timeForm.note || ''} onChange={e => setTimeForm(f => ({ ...f, note: e.target.value }))} />
      </Modal>}

      {runModal && <Modal title="New Payroll Run" onClose={() => { setRunModal(false); setPreview(null) }}
        footer={<><button className="btn btn-ghost" onClick={() => { setRunModal(false); setPreview(null) }}>Cancel</button>
          {!preview ? <button className="btn btn-primary" onClick={doPreview}>Preview</button>
            : <button className="btn btn-primary" onClick={createRun}>Create Run</button>}</>}>
        <Select label="Employee" options={[{ value: '', label: '— Select —' }, ...employees.map(e => ({ value: e.id, label: `${e.full_name} (${e.pay_type || 'no type'})` }))]} value={runForm.user_id || ''} onChange={e => { setRunForm(f => ({ ...f, user_id: e.target.value })); setPreview(null) }} />
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}><Input label="Period Start" type="date" value={runForm.period_start || ''} onChange={e => { setRunForm(f => ({ ...f, period_start: e.target.value })); setPreview(null) }} /></div>
          <div style={{ flex: 1 }}><Input label="Period End" type="date" value={runForm.period_end || ''} onChange={e => { setRunForm(f => ({ ...f, period_end: e.target.value })); setPreview(null) }} /></div>
        </div>
        <Input label="Bonus (optional, USD)" type="number" step="0.01" value={runForm.bonus || ''} onChange={e => { setRunForm(f => ({ ...f, bonus: e.target.value })); setPreview(null) }} />
        {(() => { const emp = employees.find(e => String(e.id) === String(runForm.user_id)); return emp && emp.pay_basis === 'fixed' && emp.pay_type !== 'monthly' ? <Input label={`Days Worked (leave blank for full period · expected ${emp.expected_days || 5})`} type="number" step="0.5" value={runForm.days_worked || ''} onChange={e => { setRunForm(f => ({ ...f, days_worked: e.target.value })); setPreview(null) }} /> : null })()}
        {preview && <div style={{ marginTop: 12, padding: 12, background: 'var(--bg2)', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>{preview.full_name} — {preview.pay_type}</div>
          {preview.pay_type !== 'monthly' && preview.pay_basis !== 'fixed' ? <div>Total hours: <strong>{fmtHM(preview.total_hours)}</strong> ({parseFloat(preview.total_hours).toFixed(2)}) × ${preview.pay_rate}/hr</div> : preview.pay_type !== 'monthly' && preview.pay_basis === 'fixed' ? <div>Fixed amount: <strong>${preview.pay_rate}</strong> / period</div> : null}
          <div>Gross: <strong>{money(preview.gross_amount)}</strong></div>
          {preview.bonus > 0 && <div>Bonus: {money(preview.bonus)}</div>}
          <div style={{ marginTop: 6, fontSize: 15 }}>Total: <strong style={{ color: 'var(--accent3)' }}>{money(preview.total_amount)}</strong></div>
          {preview.pay_type !== 'monthly' && preview.pay_basis !== 'fixed' && preview.entries?.length === 0 && <div style={{ color: '#F59E0B', marginTop: 6 }}>⚠ No unpaid time entries in this period</div>}
        </div>}
      </Modal>}

      {payModal && <Modal title={`Mark Paid — ${payModal.full_name}`} onClose={() => setPayModal(null)}
        footer={<><button className="btn btn-ghost" onClick={() => setPayModal(null)}>Cancel</button><button className="btn btn-primary" onClick={markPaid}>Confirm Paid</button></>}>
        <div style={{ fontSize: 13, marginBottom: 12 }}>Total: <strong>{money(payModal.total_amount)}</strong></div>
        <Input label="Paid Date" type="date" value={payForm.paid_date || ''} onChange={e => setPayForm(f => ({ ...f, paid_date: e.target.value }))} />
        <Textarea label="Payment Note (optional)" value={payForm.paid_note || ''} onChange={e => setPayForm(f => ({ ...f, paid_note: e.target.value }))} />
      </Modal>}
    </div>
  )
}
